<template>
    <div class="flex flex-col items-center w-full photos-container mb-2">
        <div class="w-full cursor-pointer">
            <input
                type="file"
                id="files-input"
                class="hidden"
                name="images"
                multiple
                accept="image/*, video/*, .cr2, .raf, .cr3, .arw, .nef, .nrw, .dng"
                @change="selectFiles"
                ref="photos-uploader-input"
            />

            <div
                :class="{
                    'bg-white/70 rounded-md border-1 border-dashed border-gray-300 w-full flex flex-col items-center justify-center hover:border-gray-400 hover:bg-white transition duration-200': true,
                    'border-solid': totalFiles > 0,
                    'drag': isDragging
                }"
                id="photo-loader"
                @click.prevent="clickSelectFiles">
                <div class="flex items-center justify-center w-full p-6 pb-5 gap-2">
                    <div class="photo-loader-icon" v-if="totalFiles === 0">
                        <ArrowUpTrayIcon class="size-8 text-gray-500" />
                    </div>
                    <div class="photo-loader-indicator" v-if="totalFiles > 0">
                        <svg version="1.1" id="L6" class="size-8" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                             viewBox="0 0 100 100" enable-background="new 0 0 100 100" xml:space="preserve">
                                                <rect fill="#f59c25" stroke="#f1f3f9" stroke-width="4" x="25" y="25" width="50" height="50">
                                                    <animateTransform
                                                        attributeName="transform"
                                                        dur="0.5s"
                                                        from="0 50 50"
                                                        to="180 50 50"
                                                        type="rotate"
                                                        id="strokeBox"
                                                        attributeType="XML"
                                                        begin="rectBox.end"/>
                                                </rect>
                            <rect x="27" y="27" fill="#f1f3f9" width="46" height="50">
                                                    <animate
                                                        attributeName="height"
                                                        dur="1.3s"
                                                        attributeType="XML"
                                                        from="50"
                                                        to="0"
                                                        id="rectBox"
                                                        fill="freeze"
                                                        begin="0s;strokeBox.end"/>
                                                </rect>
                                            </svg>
                    </div>
                    <span v-if="totalFiles > 0" class="text-gray-500">
                        {{ $t('photo_uploader.uploading_files') }} <strong>{{ uploadedFileIds.length }} {{ $t('photo_uploader.of') }} {{ totalFiles }} ({{ uploadedSizeString }} / {{ totalSizeString }})</strong>
                    </span>
                    <span v-if="totalFiles === 0" class="text-gray-500">
                        {{ $t('photo_uploader.upload_files') }}
                    </span>

                    <!-- Індикатор пам'яті (тільки для користувачів з memory_info: true) -->
                    <div v-if="shouldShowMemoryInfo && memoryInfo !== 'N/A'" class="text-xs text-gray-400 mt-1" :class="{'text-red-500': isMemoryWarning}">
                        🧠 {{ memoryInfo }}
                        <span v-if="isMemoryWarning" class="ml-1">⚠️</span>
                    </div>
                </div>
                <div :class="{'w-full bg-gray-200 h-1.5 rounded': true, 'opacity-0': totalFiles === 0}">
                    <div
                        :class="{
                          'bg-yellow-500': true
                        }"
                        :style="{ width: `${uploadingProgress}%` }"
                        class="h-full rounded-md"
                    ></div>
                </div>
            </div>
        </div>

        <div class="w-full mt-3">
            <div class="rounded-md bg-red-50 p-4" v-if="errors.length !== 0">
                <div class="flex">
                    <div class="shrink-0">
                        <XCircleIcon class="size-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3 w-full">
                        <div class="flex justify-between w-full items-start">
                            <h3 class="text-sm font-medium text-red-800">{{ $t('photo_uploader.upload_errors') }}</h3>
                        </div>
                        <div class="mt-2 text-sm text-red-700">
                            <ul role="list" class="space-y-1.5">
                                <li v-for="(error, index) in errors" :key="`loading-error-${index}`" class="flex items-center gap-1.5">
                                    <a href="#" @click.prevent="retryFileById(error.fileId)" class="bg-red-100 text-red-700 rounded-md px-1.5 py-1 text-sm cursor-pointer hover:bg-white/70">
                                        <ArrowPathIcon class="size-4" />
                                    </a>
                                    <span><b>{{ getFileNameById(error.fileId) }}</b>: {{ error.message }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <PhotoProcessingManager
            :photo-ids="uploadedPhotoIds"
            :enabled="uploadedPhotoIds.length > 0"
            @processing-complete="onProcessingComplete"
        />
    </div>

    <UploadLimitModalAlertComponent
        :open="showUploadLimitModal"
        @close="showUploadLimitModal = false"
        :type="limitUploadModalType"
    />

    <DuplicateFilesModalComponent
        :open="showDuplicatesModal"
        :filesNames="duplicateFileNames"
        @close="() => {}"
        @skip="proceedWithPendingUpload(true)"
        @upload-both="proceedWithPendingUpload(false)"
        :title="String($t('photo_uploader.duplicates_title')) || 'Знайдено файли з однаковою назвою'"
        :message="String($t('photo_uploader.duplicates_message')) || 'Деякі обрані файли вже існують у галереї. Оберіть дію:'"
        :skipText="String($t('photo_uploader.skip')) || 'Пропустити'"
        :uploadBothText="String($t('photo_uploader.upload_both')) || 'Завантажити обидва'"
    />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";
import {ResponseError} from "@/types";
import {isSafari} from "@/_helpers/functions";
import {getMemoryInfo, isMemoryLimitExceeded, logMemoryUsage, formatMemorySize} from "@/_helpers/memory";
import {reportMemoryError, reportUnknownError, uploading, uploadingError} from "@/data/requests/analytics";

import {ArrowUpTrayIcon, XCircleIcon, ArrowPathIcon} from "@heroicons/vue/20/solid";
import UploadLimitModalAlertComponent from "@/components/Galleries/Modals/UploadLimitModalAlertComponent.vue";
import DuplicateFilesModalComponent from "@/components/Galleries/Modals/DuplicateFilesModalComponent.vue";
import PhotoProcessingManager from "@/components/PhotoProcessingManager.vue";

// Інтерфейс для метаданих файлів
interface FileMetadata {
    id: string;
    name: string;
    size: number;
    type: string;
    lastModified: number;
}

export default defineComponent({
    name: "NewPhotoUploaderComponent",

    components: {
        UploadLimitModalAlertComponent,
        DuplicateFilesModalComponent,
        PhotoProcessingManager,
        ArrowUpTrayIcon,
        XCircleIcon,
        ArrowPathIcon
    },

    props: {
        folder: Object,
    },

    data() {
        return {
            errors: [] as {fileId: string, message: string}[],
            fileMetadata: [] as FileMetadata[],

            isUploadingFiles: false,

            uploadedFileIds: [] as string[],
            uploadingFileIds: new Set<string>(),
            showUploadLimitModal: false,
            limitUploadModalType: "storage_limit",
            isDragging: false,
            showDuplicatesModal: false,
            duplicateFileNames: [] as string[],
            pendingChunks: [] as File[][],
            pendingInput: null as HTMLInputElement | null,

            uploadedPhotoIds: [] as number[],

            // Кеш файлів тільки для retry
            fileCache: new Map<string, File>(),

            // Моніторинг пам'яті
            memoryCheckInterval: null as number | null,
            memoryThreshold: 85, // Поріг використання пам'яті в %
            memoryWarningShown: false,
            currentMemoryInfo: null as any, // Реактивна змінна для пам'яті
        }
    },

    computed: {
        ...mapGetters({
            gallery: "galleries/gallery",
            photos: "photos/photos",
            user: "user/user"
        }),

        totalFiles(): number {
            return this.fileMetadata.length;
        },

        totalSize(): number {
            return this.fileMetadata.reduce((sum, meta) => sum + meta.size, 0);
        },

        uploadedSize(): number {
            return this.fileMetadata
                .filter(meta => this.uploadedFileIds.includes(meta.id))
                .reduce((sum, meta) => sum + meta.size, 0);
        },

        uploadingProgress(): number {
            const total = this.totalFiles as number;
            if (!total) return 0;
            return (this.uploadedFileIds.length / total) * 100;
        },

        isSafari(): Boolean {
            return isSafari()
        },

        uploadedSizeString(): string {
            return this.bytesToSize(this.uploadedSize as number);
        },

        totalSizeString(): string {
            return this.bytesToSize(this.totalSize as number);
        },

        // Інформація про пам'ять
        memoryInfo(): string {
            if (this.currentMemoryInfo) {
                return `${formatMemorySize(this.currentMemoryInfo.usedJSHeapSize)} / ${formatMemorySize(this.currentMemoryInfo.jsHeapSizeLimit)} (${this.currentMemoryInfo.memoryUsage.toFixed(1)}%)`;
            }
            return 'N/A';
        },

        isMemoryWarning(): boolean {
            if (this.currentMemoryInfo) {
                return this.currentMemoryInfo.memoryUsage > this.memoryThreshold;
            }
            return false;
        },

        // Перевіряємо чи потрібно показувати інформацію про пам'ять
        shouldShowMemoryInfo(): boolean {
            return this.user?.memory_info === true;
        }
    },

    mounted() {
        document.body.addEventListener('dragover', this.handleDragOver);
        document.body.addEventListener('dragleave', this.handleDragLeave);
        document.body.addEventListener('drop', this.handleDrop);
        window.addEventListener('beforeunload', this.handleBeforeUnload);

        // Запускаємо моніторинг пам'яті тільки для користувачів з memory_info: true
        if (this.shouldShowMemoryInfo) {
            // Оновлюємо початкове значення
            this.updateMemoryInfo();

            this.memoryCheckInterval = window.setInterval(() => {
                this.updateMemoryInfo();
                this.checkMemoryLimit();
                logMemoryUsage('Memory check');
            }, 5000);
        }
    },

    beforeUnmount() {
        document.body.removeEventListener('dragover', this.handleDragOver);
        document.body.removeEventListener('dragleave', this.handleDragLeave);
        document.body.removeEventListener('drop', this.handleDrop);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);

        // Зупиняємо моніторинг пам'яті
        if (this.memoryCheckInterval) {
            clearInterval(this.memoryCheckInterval);
            this.memoryCheckInterval = null;
        }
    },

    methods: {
        ...mapActions({
            uploadPhoto: "photos/uploadPhotoByUrl",
        }),

        async selectFiles(event: Event) {
            const input = event.target as HTMLInputElement;
            const filesArray = Array.from(input.files as FileList);

            // Створюємо метадані замість збереження файлів
            this.fileMetadata = this.createFileMetadata(filesArray);

            this.uploadedFileIds = [];
            this.uploadingFileIds.clear();
            this.errors = [];
            this.uploadedPhotoIds = [];

            // Оновлюємо інформацію про пам'ять при виборі файлів
            if (this.shouldShowMemoryInfo) {
                this.updateMemoryInfo();
            }

            input.disabled = true;

            // Перевіряємо дублікати
            const duplicateNames = this.findDuplicateNames(filesArray);
            if (duplicateNames.length > 0) {
                this.duplicateFileNames = duplicateNames;
                this.showDuplicatesModal = true;
                this.pendingInput = input;
                return;
            }

            await this.performUpload(input, filesArray);
        },

        async proceedWithPendingUpload(skipDuplicates: boolean) {
            if (!this.pendingInput) return;

            const input = this.pendingInput;
            const duplicateSet = new Set(this.duplicateFileNames);

            if (skipDuplicates) {
                // Фільтруємо дублікати з метаданих
                this.fileMetadata = this.fileMetadata.filter((meta) => !duplicateSet.has(meta.name));
            }

            this.showDuplicatesModal = false;
            this.duplicateFileNames = [];

            // Отримуємо відфільтровані файли
            const filesArray = Array.from(input.files as FileList).filter(file =>
                skipDuplicates ? !duplicateSet.has(file.name) : true
            );

            await this.performUpload(input, filesArray);
        },

        findDuplicateNames(selectedFiles: File[]): string[] {
            const existingNames = new Set((this.photos as any[]).map((p: any) => p.original_filename));
            const duplicates: string[] = [];
            for (const file of selectedFiles) {
                if (existingNames.has(file.name)) {
                    duplicates.push(file.name);
                }
            }
            return duplicates;
        },

        async performUpload(input: HTMLInputElement, filesArray: File[]) {
            // Очищаємо input одразу
            input.value = "";

            // Перевіряємо пам'ять перед початком завантаження (тільки для користувачів з memory_info: true)
            if (this.shouldShowMemoryInfo) {
                this.updateMemoryInfo();
                this.checkMemoryLimit();
            }

            let chunks: File[][] = [];
            let chunkSize = isSafari() ? 1 : 3; // Збільшуємо розмір чанку для кращої продуктивності

            for(let i = 0; i < filesArray.length; i += chunkSize) {
                chunks.push(filesArray.slice(i, i + chunkSize));
            }

            this.isUploadingFiles = true;

            for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
                const chunk = chunks[chunkIndex];

                // Перевіряємо пам'ять перед кожним батчем (тільки для користувачів з memory_info: true)
                if (this.shouldShowMemoryInfo) {
                    this.updateMemoryInfo();
                    this.checkMemoryLimit();
                }

                // Додаємо файли чанку до списку завантажуючих
                chunk.forEach(file => {
                    const fileId = this.findFileIdByFile(file);
                    this.uploadingFileIds.add(fileId);
                });

                const promises = chunk.map(async (file) => {
                    const fileId = this.findFileIdByFile(file);
                    const startTime = performance.now();

                    try {
                        const photo = await this.uploadFile(file);

                        // Відправляємо успішне завантаження на analytics
                        uploading(
                            this.gallery?.slug || '',
                            file.size,
                            file.name,
                            this.user?.id || 0,
                            startTime,
                            performance.now(),
                            { success: true },
                            () => {},
                            () => {}
                        );

                        // Оновлюємо статус
                        this.uploadingFileIds.delete(fileId);
                        this.uploadedFileIds.push(fileId);

                        // Зберігаємо ID завантаженої фотографії
                        if (photo && photo.id) {
                            this.uploadedPhotoIds.push(photo.id);
                        }

                        // 🔥 ФАЙЛ БІЛЬШЕ НЕ ЗБЕРІГАЄТЬСЯ В КОМПОНЕНТІ!

                    } catch (error: any) {
                        this.uploadingFileIds.delete(fileId);

                        // Відправляємо помилку на analytics
                        uploadingError(
                            this.gallery?.slug || '',
                            JSON.stringify({type: 'upload_error', message: error.message}),
                            file.size,
                            file.name,
                            this.user?.id || 0,
                            startTime,
                            performance.now(),
                            () => {},
                            () => {}
                        );

                        // Відправляємо невідому помилку на analytics
                        reportUnknownError(error, {
                            galleryId: this.gallery?.id,
                            fileName: file.name,
                            fileSize: file.size,
                            memoryMonitoringEnabled: this.shouldShowMemoryInfo
                        });

                        // Зберігаємо файл в кеші тільки для можливого retry
                        this.fileCache.set(fileId, file);

                        this.errors.push({ fileId, message: error.message });
                    }
                });

                await Promise.all(promises);

                // Пауза між батчами для звільнення пам'яті
                if (chunkIndex < chunks.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            this.isUploadingFiles = false;
            input.disabled = false;

            // Очищуємо метадані через короткий час
            setTimeout(() => {
                this.fileMetadata = [];
                this.uploadedFileIds = [];
                this.uploadingFileIds.clear();
                this.fileCache.clear(); // Очищуємо кеш
            }, 3000);
        },

        // Обробка завершення обробки фотографій
        onProcessingComplete(photoIds: number[]) {
            console.log('Photo processing completed for:', photoIds);
            // Можна додати додаткову логіку, наприклад, показувати повідомлення
        },

        async uploadFile(file: File) {
            return await this.uploadPhoto({
                file,
                gallery: this.gallery?.id,
                folder: this.folder?.id
            }).catch(({type, data}) => {
                if(type == ResponseError.TooLarge) {
                    throw new Error(this.$t('photo_uploader.error_too_large'));
                }

                if(type == ResponseError.Unauthorized) {
                    throw new Error(this.$t('photo_uploader.error_unauthorized'));
                }

                if(data.data?.type === "storage_limit") {
                    this.showUploadLimitModal = true;
                    this.limitUploadModalType = data.data.type;
                }

                if(data.data?.type === "video_limit") {
                    this.showUploadLimitModal = true;
                    this.limitUploadModalType = data.data.type;
                }

                if (data.message) {
                    throw new Error(data.message);
                }

                throw new Error(this.$t('photo_uploader.error_unknown'));
            });
        },

        clickSelectFiles() {
            const input = this.$refs["photos-uploader-input"] as HTMLInputElement;
            input.click();
        },

        bytesToSize(bytes: any) {
            const num = Number(bytes);
            const sizes = [
                this.$t('storage.byte'),
                this.$t('storage.kilobyte'),
                this.$t('storage.megabyte'),
                this.$t('storage.gigabyte'),
                this.$t('storage.terabyte')
            ];

            if (num == 0) return '0 ' + this.$t('storage.byte');

            const i = (Math.floor(Math.log(num) / Math.log(1024)));

            return Math.round(num / Math.pow(1024, i)) + ' ' + sizes[i];
        },

        handleDragOver(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();
            this.isDragging = true;
        },

        handleDragLeave(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();
            this.isDragging = false;
        },

        handleDrop(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();
            this.isDragging = false;

            const files = Array.from(event.dataTransfer?.files || []);

            if (files.length) {
                const customEvent = {
                    target: {
                        files: files as unknown as FileList
                    }
                };

                this.selectFiles(customEvent as unknown as Event);
            }
        },

        handleBeforeUnload(event: BeforeUnloadEvent) {
            if (this.totalFiles > 0) {
                const message = this.$t('photo_uploader.unload_warning');
                event.returnValue = message;
                return message;
            }
        },

        // Створення метаданих файлів
        createFileMetadata(files: File[]): FileMetadata[] {
            return files.map(file => ({
                id: this.generateFileId(file),
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            }));
        },

        // Генерація унікального ID файлу
        generateFileId(file: File): string {
            return `${file.name}_${file.size}_${file.lastModified}_${Date.now()}_${Math.random()}`;
        },

        // Пошук ID файлу за об'єктом File
        findFileIdByFile(file: File): string {
            return this.fileMetadata.find(meta =>
                meta.name === file.name &&
                meta.size === file.size &&
                meta.lastModified === file.lastModified
            )?.id || '';
        },

        // Отримання імені файлу за ID
        getFileNameById(fileId: string): string {
            return this.fileMetadata.find(meta => meta.id === fileId)?.name || 'Unknown file';
        },

        // Повторне завантаження файлу за ID
        async retryFileById(fileId: string) {
            this.errors = this.errors.filter((error) => error.fileId !== fileId);

            const file = this.fileCache.get(fileId);
            if (!file) {
                console.error('File not found in cache for retry:', fileId);
                return;
            }

            // Додаємо до списку завантажуючих
            this.uploadingFileIds.add(fileId);

            try {
                const photo = await this.uploadFile(file);

                // Оновлюємо статус
                this.uploadingFileIds.delete(fileId);
                this.uploadedFileIds.push(fileId);

                if (photo && photo.id) {
                    this.uploadedPhotoIds.push(photo.id);
                }

                // Очищуємо файл з кешу після успішного завантаження
                this.fileCache.delete(fileId);

            } catch (error: any) {
                this.uploadingFileIds.delete(fileId);
                this.errors.push({
                    fileId,
                    message: error.message
                });
            }
        },

        retryAllFiles() {
            // Повторне завантаження всіх файлів з помилками
            const errorsToRetry = [...this.errors];
            this.errors = [];

            errorsToRetry.forEach(error => {
                this.retryFileById(error.fileId);
            });
        },

        // Моніторинг пам'яті

        // Оновлюємо реактивну інформацію про пам'ять
        updateMemoryInfo: function() {
            const info = getMemoryInfo();
            if (info) {
                this.currentMemoryInfo = info;
                console.log('🔄 Memory info updated:', this.memoryInfo);
            }
        },

        checkMemoryLimit: function() {
            // Перевіряємо пам'ять тільки для користувачів з memory_info: true
            if (!this.shouldShowMemoryInfo) {
                return;
            }

            if (isMemoryLimitExceeded(this.memoryThreshold)) {
                if (!this.memoryWarningShown) {
                    this.memoryWarningShown = true;

                    // Відправляємо звіт про помилку пам'яті
                    reportMemoryError(
                        `Memory usage exceeded ${this.memoryThreshold}% during upload`,
                        {
                            galleryId: this.gallery?.id,
                        },
                        () => {
                            console.log('Memory error reported to analytics');
                        },
                        (type, data) => {
                            console.error('Failed to report memory error:', type, data);
                        }
                    );

                    // Показуємо попередження користувачу
                    console.warn(`⚠️ High memory usage detected: ${this.memoryInfo}`);

                    // Пропонуємо очистити пам'ять
                    if (this.fileCache.size > 0) {
                        console.log('Clearing file cache to free memory...');
                        this.fileCache.clear();
                    }
                }
            } else {
                this.memoryWarningShown = false;
            }
        }
    },
});
</script>

<style lang="scss" scoped>
.photos-container {
    .grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    }
}
</style>
